import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../lib/api";
import { PageHead, Card, Field, Input, Textarea, Select, Btn, Loading } from "../components/kit";
import { ItemEditor, NEW_ITEM } from "../components/order/ItemEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { rupiah, todayWIB, num, PAYMENT_METHODS } from "../lib/constants";

const blank = () => ({ order_date: todayWIB(), deadline: "", customer: { id: null, name: "", whatsapp: "", address: "" }, items: [newItem()], dp: "", dp_method: "Tunai", notes: "" });
const newItem = () => ({ ...NEW_ITEM, _key: Math.random().toString(36).slice(2) });

function CustomerSection({ c, set }) {
  const { data: customers } = useApi("/customers", 0);
  const pick = (id) => {
    const x = (customers || []).find((k) => k.id === id);
    set(x ? { id: x.id, name: x.name, whatsapp: x.whatsapp, address: x.address } : { id: null, name: "", whatsapp: "", address: "" });
  };
  return (
    <Card className="space-y-3">
      <h3 className="font-bold text-lg">Data Pelanggan</h3>
      <Field label="Pilih pelanggan lama"><Select data-testid="customer-select" placeholder="+ Pelanggan baru" options={(customers || []).map((k) => [k.id, `${k.name}${k.whatsapp ? " · " + k.whatsapp : ""}`])} value={c.id || ""} onChange={(e) => pick(e.target.value)} /></Field>
      <Field label="Nama pelanggan"><Input required data-testid="customer-name-input" value={c.name} onChange={(e) => set({ ...c, name: e.target.value })} /></Field>
      <Field label="No. WhatsApp"><Input type="tel" inputMode="tel" data-testid="customer-wa-input" value={c.whatsapp} onChange={(e) => set({ ...c, whatsapp: e.target.value })} placeholder="08xxxxxxxxxx" /></Field>
      <Field label="Alamat"><Textarea rows={2} data-testid="customer-address-input" value={c.address} onChange={(e) => set({ ...c, address: e.target.value })} /></Field>
    </Card>
  );
}

function StockWarning({ list, onCancel, onConfirm, busy }) {
  return (
    <Dialog open={!!list} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="stock-warning-dialog" className="rounded-2xl">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-600"><AlertTriangle className="w-6 h-6" /> Stok cup polos tidak cukup</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {(list || []).map((s) => (
            <div key={s.cup_type + s.cup_size} className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm">
              <div className="font-bold">{s.cup_type} {s.cup_size}{!s.registered && " (belum terdaftar di stok)"}</div>
              <div className="font-mono">Butuh {num(s.needed)} · Tersedia {num(s.available)} · Kurang {num(s.needed - s.available)}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 pt-2">
          <Btn variant="secondary" onClick={onCancel} data-testid="stock-warning-cancel">Batal</Btn>
          <Btn variant="primary" onClick={onConfirm} loading={busy} data-testid="stock-warning-confirm">Tetap Simpan</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const toPayload = (f) => ({
  ...f, dp: Number(f.dp) || 0,
  items: f.items.map((i) => ({ ...i, qty: Number(i.qty), colors: Number(i.colors) || 1, unit_price: Number(i.unit_price) || 0 })),
});

const withKeys = (o) => ({ order_date: o.order_date, deadline: o.deadline, customer: o.customer, items: o.items.map((it) => ({ ...it, _key: Math.random().toString(36).slice(2) })), dp: o.dp, notes: o.notes, paid: o.paid });

function useOrderDraft(id) {
  const [f, setF] = useState(id ? null : blank());
  useEffect(() => {
    if (id) api.get(`/orders/${id}`).then(({ data }) => setF(withKeys(data)));
  }, [id]);
  return [f, setF];
}

function PaymentSection({ id, f, setF, total, paid, methods }) {
  return (
    <Card className="space-y-3">
      <h3 className="font-bold text-lg">Pembayaran</h3>
      <div className="flex justify-between"><span className="text-slate-500">Total harga</span><span className="font-mono font-bold text-xl" data-testid="order-total">{rupiah(total)}</span></div>
      {!id && (
        <div className="grid grid-cols-[1fr_auto] gap-3">
          <Field label="DP dibayar (Rp)"><Input type="number" inputMode="decimal" min="0" max={total} value={f.dp} onChange={(e) => setF({ ...f, dp: e.target.value })} data-testid="order-dp-input" /></Field>
          <Field label="Metode"><Select options={methods} value={f.dp_method} onChange={(e) => setF({ ...f, dp_method: e.target.value })} className="w-32" data-testid="order-dp-method-select" /></Field>
        </div>
      )}
      {id && <div className="flex justify-between"><span className="text-slate-500">Sudah dibayar</span><span className="font-mono font-bold">{rupiah(paid)}</span></div>}
      <div className="flex justify-between pt-2 border-t"><span className="text-slate-500">Sisa tagihan</span><span className="font-mono font-bold text-xl text-rose-600" data-testid="order-remaining">{rupiah(total - paid)}</span></div>
    </Card>
  );
}

function useOrderSubmit(id, f, nav) {
  const [warn, setWarn] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = id ? await api.put(`/orders/${id}`, toPayload(f)) : await api.post("/orders", toPayload(f));
      toast.success(id ? "Order diperbarui" : `Order ${data.order.order_no} tersimpan`);
      nav(`/order/${data.order.id}`, { replace: true });
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); setWarn(null); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (f.deadline < f.order_date) return toast.error("Deadline tidak boleh sebelum tanggal order");
    setBusy(true);
    try {
      const { data } = await api.post("/orders/check-stock", { items: toPayload(f).items });
      setBusy(false);
      if (data.shortages.length) return setWarn(data.shortages);
      await save();
    } catch (x) { setBusy(false); toast.error(errMsg(x)); }
  };
  return { warn, busy, save, submit, cancelWarn: () => setWarn(null) };
}

export default function OrderForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [f, setF] = useOrderDraft(id);
  const { data: cups } = useApi("/items/cups", 0);
  const { data: opt } = useApi("/settings/options", 0);
  const { warn, busy, save, submit, cancelWarn } = useOrderSubmit(id, f, nav);
  if (!f) return <Loading />;

  const total = f.items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const paid = id ? f.paid : Number(f.dp) || 0;
  const setItem = (i, v) => setF({ ...f, items: f.items.map((x, j) => (j === i ? v : x)) });

  return (
    <form onSubmit={submit} className="space-y-5 max-w-3xl" data-testid="order-form">
      <PageHead title={id ? "Edit Order" : "Order Baru"} sub={id ? "Ubah data order" : "Nomor order dibuat otomatis saat disimpan"} />
      <Card className="grid grid-cols-2 gap-3">
        <Field label="Tanggal order"><Input type="date" required value={f.order_date} onChange={(e) => setF({ ...f, order_date: e.target.value })} data-testid="order-date-input" /></Field>
        <Field label="Deadline"><Input type="date" required value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} data-testid="order-deadline-input" /></Field>
      </Card>
      <CustomerSection c={f.customer} set={(customer) => setF({ ...f, customer })} />
      <div className="space-y-3">
        <h3 className="font-bold text-lg">Detail Pesanan</h3>
        {f.items.map((it, i) => (
          <ItemEditor key={it._key} idx={i} item={it} onChange={(v) => setItem(i, v)} cupTypes={(cups || []).map((c) => c.cup_type)} cupSizes={(cups || []).map((c) => c.cup_size)}
            onRemove={f.items.length > 1 ? () => setF({ ...f, items: f.items.filter((_, j) => j !== i) }) : null} />
        ))}
        <Btn type="button" variant="secondary" className="w-full" onClick={() => setF({ ...f, items: [...f.items, newItem()] })} data-testid="add-item-btn"><Plus className="w-5 h-5" /> Tambah Item</Btn>
      </div>
      <PaymentSection id={id} f={f} setF={setF} total={total} paid={paid} methods={opt?.payment_methods || PAYMENT_METHODS} />
      <Card><Field label="Catatan khusus"><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="order-notes-input" /></Field></Card>
      <div className="sticky bottom-20 lg:bottom-4 z-20">
        <Btn type="submit" loading={busy} className="w-full h-14 text-lg" data-testid="order-submit-btn"><Save className="w-5 h-5" /> Simpan Order</Btn>
      </div>
      <StockWarning list={warn} busy={busy} onCancel={cancelWarn} onConfirm={save} />
    </form>
  );
}
